import { useCallback, useState, useSyncExternalStore } from "react"

const EMPTY: ReadonlySet<string> = new Set()

const sameMembers = (a: ReadonlySet<string>, b: ReadonlySet<string>) => {
  if (a === b) return true
  if (a.size !== b.size) return false
  for (const id of a) if (!b.has(id)) return false
  return true
}

/**
 * A subscribable "which rows are highlighted" value, for cells that must re-render when the
 * highlight moves onto or off their own row.
 *
 * Not React state: the table's ref stays the source of truth because ag-grid reads it during its
 * own render pass, and state would land in the column-def memo's deps, re-processing
 * `autoGroupColumnDef` on every row click. The table mirrors each ref write here, and only the
 * cells that care wake up. The identity never changes, so it is safe in memo deps.
 */
export type HighlightSubscription = {
  /** Reads the currently highlighted row ids. */
  get: () => ReadonlySet<string>
  /** Writes the highlight and wakes subscribers. Called from `applyHighlight`, nowhere else. */
  set: (rowIds: ReadonlySet<string>) => void
  subscribe: (listener: () => void) => () => void
}

const createHighlightSubscription = (): HighlightSubscription => {
  let current: ReadonlySet<string> = EMPTY
  const listeners = new Set<() => void>()
  return {
    get: () => current,
    // By membership, not identity: `applyHighlight` builds a fresh Set each move.
    set: (rowIds) => {
      if (sameMembers(current, rowIds)) return
      current = rowIds
      listeners.forEach((listener) => listener())
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

/** Built once per table and never replaced, so it is safe in a memo's dependency list. */
export const useHighlightSubscription = (): HighlightSubscription => {
  const [subscription] = useState(createHighlightSubscription)
  return subscription
}

/**
 * True while the row is highlighted; re-renders only rows entering or leaving the set. `rowIds`
 * is the leaf's own id or a group's leaf ids (a fresh array each render is fine). The snapshot
 * stays a boolean: returning the set would loop `useSyncExternalStore`.
 */
export const useIsHighlightedRow = (
  subscription: HighlightSubscription,
  rowIds: string | readonly string[],
): boolean => {
  // A primitive key, so a fresh array each render doesn't rebuild the callback and resubscribe.
  const key = typeof rowIds === "string" ? rowIds : rowIds.join("\u0000")
  const isGroup = typeof rowIds !== "string"
  const getSnapshot = useCallback(() => {
    if (key === "") return false
    const highlighted = subscription.get()
    const ids = key.split("\u0000")
    // A leaf shows while in the highlight; a group only when the highlight is exactly its leaves
    // (a subset test would light every group inside a larger selection).
    if (isGroup && highlighted.size !== ids.length) return false
    return ids.every((id) => highlighted.has(id))
  }, [subscription, key, isGroup])
  return useSyncExternalStore(subscription.subscribe, getSnapshot, getSnapshot)
}
